import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';
import './ChatPage.css';

interface QuickReply {
  label: string;
  value: string;
  action: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  quickReplies?: QuickReply[];
  isBookingConfirmation?: boolean;
  businessName?: string;
}

interface AgentInfo {
  id: string;
  businessName: string;
  businessType: string;
  businessDescription: string;
  tone: string;
  calendarEnabled?: boolean;
  socialLinks?: Array<{
    platform: string;
    url: string;
    label?: string;
  }>;
  logoUrl?: string;
  tagline?: string;
  suggestedQuestions?: string[];
  locationInfo?: {
    phone?: string;
    address?: {
      formatted?: string;
    } | string;
    timezone?: string;
  };
  operatingHours?: {
    enabled: boolean;
    timezone: string;
    schedule: Record<string, { enabled: boolean; start: string; end: string }>;
  };
}

const ChatPage: React.FC = () => {
  const { handle } = useParams<{ handle: string }>();
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [agentInfo, setAgentInfo] = useState<AgentInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Helper function to get social media icon and display name
  const getSocialIcon = (platform: string) => {
    const iconProps = { className: "w-5 h-5", fill: "currentColor", viewBox: "0 0 24 24" };

    switch (platform) {
      case 'instagram':
        return {
          icon: <svg {...iconProps}><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>,
          name: 'Instagram'
        };
      case 'facebook':
        return {
          icon: <svg {...iconProps}><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
          name: 'Facebook'
        };
      case 'twitter':
        return {
          icon: <svg {...iconProps}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
          name: 'X'
        };
      case 'tiktok':
        return {
          icon: <svg {...iconProps}><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>,
          name: 'TikTok'
        };
      case 'linkedin':
        return {
          icon: <svg {...iconProps}><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
          name: 'LinkedIn'
        };
      case 'youtube':
        return {
          icon: <svg {...iconProps}><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
          name: 'YouTube'
        };
      case 'whatsapp':
        return {
          icon: <svg {...iconProps}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>,
          name: 'WhatsApp'
        };
      case 'website':
        return {
          icon: <svg {...iconProps} fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/></svg>,
          name: 'Website'
        };
      case 'custom':
        return {
          icon: <svg {...iconProps} fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/></svg>,
          name: 'Link'
        };
      default:
        return {
          icon: <svg {...iconProps} fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/></svg>,
          name: platform
        };
    }
  };

  // Helper function to convert 24-hour time to 12-hour format
  const formatTime12Hour = (time24: string): string => {
    const [hour, minute] = time24.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
  };

  // Helper function to format relative timestamps
  const getRelativeTime = (timestamp: Date): string => {
    const now = new Date();
    const diff = now.getTime() - new Date(timestamp).getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  // Helper function to check if business is currently open
  const getBusinessStatus = () => {
    if (!agentInfo?.operatingHours?.enabled || !agentInfo?.operatingHours?.schedule) {
      return null;
    }

    const now = new Date();
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = days[now.getDay()];
    const todaySchedule = agentInfo.operatingHours.schedule[today];

    if (!todaySchedule || !todaySchedule.enabled) {
      // Find next open day
      for (let i = 1; i <= 7; i++) {
        const nextDayIndex = (now.getDay() + i) % 7;
        const nextDay = days[nextDayIndex];
        const nextSchedule = agentInfo.operatingHours.schedule[nextDay];
        if (nextSchedule?.enabled) {
          const dayName = nextDay.charAt(0).toUpperCase() + nextDay.slice(1);
          return {
            isOpen: false,
            message: `Closed • Opens ${i === 1 ? 'tomorrow' : dayName} at ${formatTime12Hour(nextSchedule.start)}`
          };
        }
      }
      return { isOpen: false, message: 'Closed' };
    }

    // Check if currently within hours
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const [startHour, startMin] = todaySchedule.start.split(':').map(Number);
    const [endHour, endMin] = todaySchedule.end.split(':').map(Number);
    const startTime = startHour * 60 + startMin;
    const endTime = endHour * 60 + endMin;

    if (currentTime >= startTime && currentTime < endTime) {
      return { isOpen: true, message: `Open Now • Until ${formatTime12Hour(todaySchedule.end)}` };
    } else if (currentTime < startTime) {
      return { isOpen: false, message: `Closed • Opens at ${formatTime12Hour(todaySchedule.start)}` };
    } else {
      // Closed for today, find next open day
      for (let i = 1; i <= 7; i++) {
        const nextDayIndex = (now.getDay() + i) % 7;
        const nextDay = days[nextDayIndex];
        const nextSchedule = agentInfo.operatingHours.schedule[nextDay];
        if (nextSchedule?.enabled) {
          const dayName = nextDay.charAt(0).toUpperCase() + nextDay.slice(1);
          return {
            isOpen: false,
            message: `Closed • Opens ${i === 1 ? 'tomorrow' : dayName} at ${formatTime12Hour(nextSchedule.start)}`
          };
        }
      }
      return { isOpen: false, message: 'Closed' };
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Load agent info when component mounts
    const abortController = new AbortController();
    
    const loadAgentInfo = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/agent/${handle}`, {
          signal: abortController.signal
        });
        if (response.ok) {
          const data = await response.json();
          setAgentInfo(data);
          // Add welcome message
          setMessages([{
            role: 'assistant',
            content: `Hi, my name is Svarra, ${data.businessName}'s assistant. How can I help you today?`,
            timestamp: new Date()
          }]);
        } else {
          setError('Agent not found. Please check the URL.');
        }
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError('Failed to load agent information.');
        }
      }
    };

    if (handle) {
      loadAgentInfo();
    }

    return () => {
      abortController.abort();
    };
  }, [handle]);

  const sendMessage = async () => {
    if (!newMessage.trim() || isLoading) return;

    // Check if user has subscription access
    if (user && !user.hasActiveSubscription && !user.isInGracePeriod) {
      setShowSubscriptionModal(true);
      return;
    }

    const userMessage: Message = {
      role: 'user',
      content: newMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsLoading(true);

    try {
      // Generate session ID if it doesn't exist
      const currentSessionId = sessionId || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      if (!sessionId) {
        setSessionId(currentSessionId);
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/${handle}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: newMessage,
          conversationId: currentSessionId,
          sessionId: currentSessionId
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Handle potential JSON string in reply
        let content = data.reply;
        let quickReplies = data.quickReplies;
        
        // Check if reply is a JSON string (legacy compatibility)
        if (typeof content === 'string' && content.startsWith('{')) {
          try {
            const parsed = JSON.parse(content);
            if (parsed.text) {
              content = parsed.text;
              quickReplies = parsed.quickReplies || quickReplies;
            }
          } catch (e) {
            // If parsing fails, use content as-is
          }
        }
        
        const assistantMessage: Message = {
          role: 'assistant',
          content: content,
          timestamp: new Date(),
          quickReplies: quickReplies || undefined,
          isBookingConfirmation: data.bookingConfirmed || false,
          businessName: data.businessName
        };
        
        // Log successful booking confirmations
        if (data.bookingConfirmed) {
          logger.info('Booking confirmation displayed', {
            component: 'ChatPage',
            businessName: data.businessName,
            sessionId: sessionId
          });
        }
        
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (err) {
      logger.componentError('ChatPage', err, { 
        action: 'sendMessage', 
        handle, 
        sessionId 
      });
      const errorMessage: Message = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleQuickReply = async (quickReply: QuickReply) => {
    if (isLoading) return;

    // Check subscription access
    if (user && !user.hasActiveSubscription && !user.isInGracePeriod) {
      setShowSubscriptionModal(true);
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/${handle}/quick-reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: quickReply.action,
          value: quickReply.value,
          sessionId: sessionId,
          conversationId: sessionId // Use sessionId as conversationId for consistency
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Add user message showing what was selected
        const userMessage: Message = {
          role: 'user',
          content: quickReply.label,
          timestamp: new Date()
        };
        
        // Add assistant response
        const assistantMessage: Message = {
          role: 'assistant',
          content: data.message,
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, userMessage, assistantMessage]);
      } else {
        throw new Error('Failed to process quick reply');
      }
    } catch (err) {
      logger.componentError('ChatPage', err, { 
        action: 'handleQuickReply', 
        handle, 
        sessionId,
        quickReplyAction: quickReply.action 
      });
      const errorMessage: Message = {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your selection. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  if (error) {
    return (
      <div className="chat-error">
        <h2>Oops!</h2>
        <p>{error}</p>
        <a href="/">← Back to Home</a>
      </div>
    );
  }

  if (!agentInfo) {
    return (
      <div className="chat-loading">
        <div className="loading-spinner"></div>
        <p>Loading chat...</p>
      </div>
    );
  }

  const businessStatus = getBusinessStatus();
  const addressText = typeof agentInfo.locationInfo?.address === 'string'
    ? agentInfo.locationInfo.address
    : agentInfo.locationInfo?.address?.formatted;

  return (
    <div className="chat-container">
      {/* Chat Header */}
      <div className="chat-header">
        <div className="chat-header-content">
          {/* Logo */}
          {agentInfo.logoUrl ? (
            <img src={agentInfo.logoUrl} alt={`${agentInfo.businessName} logo`} className="business-logo" />
          ) : (
            <div className="business-logo-fallback">
              {agentInfo.businessName.charAt(0).toUpperCase()}
            </div>
          )}

          <div className="business-info">
            <h1>{agentInfo.businessName}</h1>
            <p className="business-type">{agentInfo.businessType.charAt(0).toUpperCase() + agentInfo.businessType.slice(1)}</p>

            {/* Business Tagline */}
            {agentInfo.tagline && (
              <p className="business-tagline">{agentInfo.tagline}</p>
            )}

            {/* Business Status Badge */}
            {businessStatus && (
              <div className={`business-status-badge ${businessStatus.isOpen ? 'status-open' : 'status-closed'}`}>
                <span className="status-dot"></span>
                {businessStatus.message}
              </div>
            )}

            {/* Contact Info Row */}
            <div className="contact-info-row">
              {/* Phone Number */}
              {agentInfo.locationInfo?.phone && (
                <a
                  href={`tel:${agentInfo.locationInfo.phone}`}
                  className="business-phone"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {agentInfo.locationInfo.phone}
                </a>
              )}

              {/* Location */}
              {addressText && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="business-location"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {addressText}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="chat-messages">
        {/* Suggested Questions (shown when only welcome message exists) */}
        {messages.length === 1 && messages[0].role === 'assistant' && agentInfo.suggestedQuestions && agentInfo.suggestedQuestions.length > 0 && (
          <div className="suggested-questions">
            <p className="suggested-questions-title">Get started with a question:</p>
            <div className="suggested-questions-grid">
              {agentInfo.suggestedQuestions.map((question, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setNewMessage(question);
                    setTimeout(() => sendMessage(), 100);
                  }}
                  className="suggested-question-button"
                  disabled={isLoading}
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message, index) => (
          <div
            key={index}
            className={`message ${message.role === 'user' ? 'message-user' : 'message-assistant'}`}
          >
            <div className="message-content">
              {message.content}
            </div>
            
            {/* Booking Confirmation UI */}
            {message.isBookingConfirmation && (
              <div className="booking-confirmation-card">
                <div className="booking-confirmation-header">
                  <div className="booking-confirmation-icon">
                    <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="booking-confirmation-title">
                    <h4>🎉 Appointment Confirmed!</h4>
                    <p>Your booking with {message.businessName || agentInfo?.businessName} has been confirmed.</p>
                  </div>
                </div>
                <div className="booking-confirmation-actions">
                  <div className="booking-confirmation-note">
                    <p>💌 Check your email for confirmation details and any additional instructions.</p>
                  </div>
                </div>
              </div>
            )}
            
            {message.quickReplies && message.quickReplies.length > 0 && (
              <div className="quick-replies">
                {message.quickReplies.map((quickReply, qrIndex) => (
                  <button
                    key={qrIndex}
                    onClick={() => handleQuickReply(quickReply)}
                    disabled={isLoading}
                    className="quick-reply-button"
                  >
                    {quickReply.label}
                  </button>
                ))}
              </div>
            )}
            <div className="message-time">
              {getRelativeTime(message.timestamp)}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="message message-assistant">
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Social Links Bar */}
      {agentInfo?.socialLinks && agentInfo.socialLinks.length > 0 && (
        <div className="social-links-bar">
          <div className="social-links-container">
            {agentInfo.socialLinks.map((link, index) => {
              const { icon, name } = getSocialIcon(link.platform);
              const displayName = link.label || name;

              return (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link-button"
                  title={displayName}
                >
                  {icon}
                  <span className="social-link-name">{displayName}</span>
                </a>
              );
            })}
          </div>
        </div>
      )}

      <div className="chat-input-container">
        <div className="chat-input-wrapper">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={`Ask ${agentInfo?.businessName?.trim()} anything...`}
            className="chat-input"
            rows={1}
            disabled={isLoading}
          />
          <button
            onClick={sendMessage}
            disabled={!newMessage.trim() || isLoading}
            className="send-button"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22,2 15,22 11,13 2,9"></polygon>
            </svg>
          </button>
        </div>
      </div>

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              {/* Warning Icon */}
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>

              {/* Content */}
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Subscription Required
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Your subscription has expired. Please resubscribe to continue using AI chat features.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Existing agents remain available for subscribers only.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="/#pricing"
                  className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white text-sm font-medium rounded-lg transition-all duration-300"
                >
                  Resubscribe Now
                </a>
                <button
                  onClick={() => setShowSubscriptionModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm font-medium rounded-lg transition-all duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;